import {merge} from 'lodash';

import readingRecordResponse from './mock/readingRecordData';
import readingPlanResponse from './mock/readingPlanData';
import optionsDataResponse from './mock/optionsData';
import {
    checkForError,
    transformToJSON,
    mockResponse
} from './responseHelper';
import auth from '../modules/auth';
import {normaliseReadingLog, normaliseReadingPlan} from './dataCleanupUtil';
const authToken = auth().authToken;

// base URLs as constants for consistency
const STUDENT_BASE = '/prcws/student';
const READING_RECORD_BASE = '/prcws/student_reading_record';
const READING_PLAN_BASE = '/prcws/student_reading_plan';
const DISCOVERY_GENRES = '/prcws/genres';
const CHALLENGE_RULES = '/prcws/challenge_rules';
const SUBMIT_SURVEY = '/prcws/challenge_survey';
const SCHOOL_LIST = '/prcws/school_list';
const LOGON_ENABLED = '/prcws/logon_enabled';

const mockForApiCall = (uri, options) => {
  let importPromise = import('./mock/studentData');

  if (uri.startsWith(READING_RECORD_BASE)) {
      return readingRecordResponse(options)
  }

  if (uri.startsWith(READING_PLAN_BASE)) {
      return readingPlanResponse(options)
  }

  if (uri.startsWith(DISCOVERY_GENRES)) {
      return optionsDataResponse(DISCOVERY_GENRES)
  }

  if (uri.startsWith(CHALLENGE_RULES)) {
      return optionsDataResponse(CHALLENGE_RULES)
  }

  if (uri.startsWith(SUBMIT_SURVEY)) {
      return optionsDataResponse(SUBMIT_SURVEY, options)
  }

  if (uri.startsWith(LOGON_ENABLED)) {
        return optionsDataResponse(LOGON_ENABLED)
  }

  if (uri.startsWith(SCHOOL_LIST)) {
      const mockUpSchoolList = {
          schools: [
              {schoolName: "Aberdeen Public School" , schoolId: 14242},
              {schoolName: "School 2" , schoolId: 14243},
              {schoolName: "School 3" , schoolId: 14244},
          ]
      };
      return Promise.resolve(mockResponse('application/json', mockUpSchoolList))
  }

  return importPromise.then((module) => module.default);
};

const toApiPayload = (book) => JSON.stringify(
    String(process.env.REACT_APP_MOCK_API) === 'true' ?
        book :
        {
            isbn: book.isbn,
            bookId: book.bookId,
        }
);

class StudentApi {
  constructor(token) {
    this.token = token;
  }

  _defaultHeaders() {
    return {
      headers: {
        PRCWSAuth: this.token,
        Accept: 'application/json', // default to accepting JSON
      }
    };
  }

  _makeAuthenticatedRequest(uri, options = {}) {
      if (!this.token) {
          return Promise.reject(new Error('No auth token found'))
      } else {
          const fetchPromise = (String(process.env.REACT_APP_MOCK_API) === 'true') ?
              mockForApiCall(uri, options) :
              fetch(uri, merge({}, this._defaultHeaders(), options));

          return fetchPromise
              // fetch doesn't reject the promise for HTTP error statuses, it returns ok = false
              .then(checkForError)
              .then(transformToJSON);
      }
  }

  _makeRequest(uri){
      const fetchPromise = (String(process.env.REACT_APP_MOCK_API) === 'true') ?
          mockForApiCall(uri) :
          fetch(uri, merge({}, this._defaultHeaders()));
      return fetchPromise
          .then(checkForError)
          .then(transformToJSON);
  }

  // Student

  getStudentDetails() {
    return this._makeAuthenticatedRequest(STUDENT_BASE)
        .then(studentDetails => ({
            ...studentDetails,
            srr: normaliseReadingLog(studentDetails.srr),
        }));
  }

  // Reading plan

  getReadingPlan() {
    return this._makeAuthenticatedRequest(READING_PLAN_BASE)
        .then(normaliseReadingPlan);
  }

  saveBookToPlan(book) {
    const body = toApiPayload(book);
    return this._makeAuthenticatedRequest(READING_PLAN_BASE, {method: 'POST', headers: {'Content-Type': 'application/json'}, body})
        .then(normaliseReadingPlan);
  }

  removeBookFromPlan(bookId) {
    return this._makeAuthenticatedRequest(`${READING_PLAN_BASE}/${bookId}`, {method: 'DELETE', bookId})
        .then(normaliseReadingPlan);
  }

  // Reading record

  addPrcBookToLog(book) {
    const body = toApiPayload(book);
    return this._makeAuthenticatedRequest(`${READING_RECORD_BASE}/prc`, {method: 'POST', headers: {'Content-Type': 'application/json'}, body})
        .then(normaliseReadingLog);
  }

  addNonPrcBookToLog(book) {
    // personal choice book has a different structure
    const { title, author, bookId } = book;
    const body = JSON.stringify({ title, author, bookId });
    return this._makeAuthenticatedRequest(`${READING_RECORD_BASE}/nonprc`, {method: 'POST', headers: {'Content-Type': 'application/json'}, body})
        .then(normaliseReadingLog);

  }

  removeBookFromLog(bookId) {
    return this._makeAuthenticatedRequest(`${READING_RECORD_BASE}/${bookId}`, {method: 'DELETE', bookId})
        .then(normaliseReadingLog);
  }

  //Book Discovery

  getBookGenres() {
      return this._makeRequest(DISCOVERY_GENRES)
  }

  getChallengeLevel() {
      return this._makeRequest(CHALLENGE_RULES)
  }

  //Survey
  submitStudentSurvey(surveyData) {
      const body = JSON.stringify(surveyData);
      return this._makeAuthenticatedRequest(SUBMIT_SURVEY, {method: 'POST', headers: {'Content-Type': 'application/json'}, body})
  }

  fetchSchoolList() {
      return this._makeAuthenticatedRequest(SCHOOL_LIST)
  }

  getLogonEnabled() {
      return this._makeRequest(LOGON_ENABLED)
  }

}

export default new StudentApi(authToken);
